import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { trackifyPageChange } from "@allianz/tracking";

//* NDBX components
import {
	Button,
	ButtonStack,
	LabelTooltip,
	NotificationCard,
	RHFCircleToggleGroupChildren,
	Section,
} from "@arp/react-ndbx";

//* MUI Components
import useTheme from "@mui/material/styles/useTheme";
import { Box, Fade, Paper, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";

//* Life Components
import Page from "../../../components/Page";
import SaveScriptModal from "../../../components/pageLevel/saveScriptModal/SaveScriptModal";

//** Config */
import {
	aboutYouDisclosureDisclaimer,
	retrieveQuoteNotification,
} from "../../../config/disclaimers/disclaimers";
import { Application } from "../../../config/const/enums";
import { questionTypes } from "../../../config/questions/questionItems";
import { dodIndividualCoverTooltips } from "../../../config/tooltips/pageTooltips";
import { aboutYouDisclosureFieldNames } from "../../../config/pageInfo/pageFieldName";
import { dodIndividualCoverNotification } from "../../../config/notifications/pageNotifications";

//** Hooks */
import useSharedData from "../../../hooks/useSharedData.ts";
import { useGetQuote } from "../../../hooks/dataHooks/useGetQuoteData";
import usePreQualification from "../../../hooks/usePreQualification.ts";
import usePreQualificationPages from "../../../hooks/usePreQualificationPages.ts";
import { useGetAuraInterviewSession } from "../../../hooks/dataHooks/useConvertQuoteAuraSession";

//-----------------------------------------------------------------------

function AboutYouDisclosure() {
	const {
		isDirty: [isDirty],
		isValid: [isValid],
		errors: [...errors],
	} = useOutletContext();

	const { state } = usePreQualificationPages();

	const { currentPage } = { ...state };
	const { dataSet } = usePreQualification();
	const { personOne, application } = { ...dataSet };
	const { data: quoteData, isLoading: quoteIsLoading } = useGetQuote();
	const [uwIndividualCoverError, setUwIndividualCoverError] = useState(false);
	const theme = useTheme();
	const circularProgressStyle = {
		color: `${theme.palette.action.PrimaryAction450}`,
	};

	useEffect(() => {
		const errorStatusUWCover =
			aboutYouDisclosureFieldNames().ABOUT_YOU_DISCLOSURE_TC in errors[0];
		setUwIndividualCoverError(errorStatusUWCover);
	}, [errors]);

	useEffect(() => {
		trackifyPageChange(currentPage?.identifier?.toLowerCase());
	}, []);

	// PART - 1
	const { accessToken, retrieveQuoteJourney } = useSharedData();
	// const azurAccessToken = getAzureJWTToken();
	const { isLoading, isError, data, error, refetch } =
		useGetAuraInterviewSession(accessToken);
	// useGetAuraInterviewSession(azurAccessToken);

	const isCC = application === Application?.CONTACT_CENTER;

	//** Modal Script */
	const [openSaveModal, setOpenSaveModal] = useState(false);
	const handleSaveModalOpen = () => {
		setOpenSaveModal(true);
	};
	const handleSaveModalClose = (event, reason) => {
		if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
			setOpenSaveModal(false);
		}
	};

	return (
		<Page title={currentPage.title} identifier={currentPage.identifier}>
			<Section mb={{ xs: 4, md: 8 }} mt={2}>
				<Typography variant="h2-bold">About you</Typography>
			</Section>
			<Section md={7} mb={1}>
				<Typography variant="label-large-regular">
					Hi {personOne?.firstName}, this is your application progress
				</Typography>
			</Section>
			<Section md={7} mb={4}>
				<Typography variant="label-large-bold">
					Quote reference: {quoteData?.quoteInformation?.quoteNumber}
				</Typography>
			</Section>

			{!isLoading ? (
				<>
					{/* show the notification in case of retrieve journey via CC */}
					{retrieveQuoteJourney && (
						<Section md={7} mb={4}>
							<Fade in={true} timeout={500}>
								<Box>
									<NotificationCard
										cardType="info"
										title="Application retrieval important information"
										message={
											isCC
												? retrieveQuoteNotification().DISCLAIMER_TEXT_REVIEW_CC(
														dataSet["personOne"].emailAddress,
														dataSet["personOne"].contactNumber
													)
												: retrieveQuoteNotification()
														.DISCLAIMER_TEXT_REVIEW_ONLINE
										}
									/>
								</Box>
							</Fade>
						</Section>
					)}
					{isCC && (
						<Section md={7} mb={4}>
							<Fade in={true} timeout={500}>
								<Box>
									<NotificationCard
										cardType="info"
										title=""
										message={aboutYouDisclosureDisclaimer.CC_NOTIFICATION_TEXT}
									/>
								</Box>
							</Fade>
						</Section>
					)}
					<Section md={7} mb={{ xs: 5, md: 8 }}>
						<Paper
							elevation={0}
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								pl: { xs: 0, sm: 10, md: 3 },
								pr: { xs: 0, sm: 10, md: 3 },
							}}
						>
							<Section mb={{ xs: 2, md: 3 }} sx={{ lineHeight: 0 }}>
								<Typography variant="p3">
									{isCC
										? aboutYouDisclosureDisclaimer.DISCLAIMER_TEXT_CC
										: aboutYouDisclosureDisclaimer.DISCLAIMER_TEXT}
								</Typography>
							</Section>
							<Section mb={{ xs: 2, md: 3 }} sx={{ lineHeight: 0 }}>
								<Typography variant="p3">
									{isCC
										? aboutYouDisclosureDisclaimer.DISCLAIMER_TEXT_UNSURE_CC
										: aboutYouDisclosureDisclaimer.DISCLAIMER_TEXT_UNSURE}
								</Typography>
							</Section>
							<Section>
								<RHFCircleToggleGroupChildren
									variant="circle-group"
									name="aboutYouDisclosureTc"
									items={questionTypes.YES_NO}
									minHeight={50}
									error={uwIndividualCoverError}
								>
									<Typography
										sx={{
											width: "fit-content !important",
											marginBottom: 2,
											textAlign: "center",
											marginTop: 1 / 2,
										}}
										variant="p3"
									>
										{aboutYouDisclosureDisclaimer.DISCLAIMER_TEXT_SELF}
									</Typography>
									<Box>
										<LabelTooltip
											controlIdentifier="dod-checkbox-tooltip"
											tooltipTitle={
												dodIndividualCoverTooltips.INDIVIDUAL_COVER_TOOLTIP
											}
										/>
									</Box>
								</RHFCircleToggleGroupChildren>
							</Section>
							{isCC ? (
								<Section md={12} mb={{ xs: 2, md: 3 }} sx={{ lineHeight: 0 }}>
									<Box mb={3} mt={1}>
										<Typography variant="p3-bold">
											{
												aboutYouDisclosureDisclaimer
													.INSTRUCTIONS_FOR_CUSTOMER_CC.title
											}
										</Typography>
									</Box>
									<Box>
										<Typography variant="p3">
											{
												aboutYouDisclosureDisclaimer
													.INSTRUCTIONS_FOR_CUSTOMER_CC.note
											}
										</Typography>
									</Box>
								</Section>
							) : null}
							{uwIndividualCoverError && (
								<Section>
									<Fade in={true} timeout={500}>
										<Box>
											<NotificationCard
												cardType={
													dodIndividualCoverNotification
														.INDIVIDUAL_COVER_NOTIFICATION.cardType
												}
												title={
													dodIndividualCoverNotification
														.INDIVIDUAL_COVER_NOTIFICATION.title
												}
												message={
													dodIndividualCoverNotification
														.INDIVIDUAL_COVER_NOTIFICATION.message
												}
											/>
										</Box>
									</Fade>
								</Section>
							)}
						</Paper>
					</Section>
				</>
			) : (
				<Section
					minHeight="300px"
					display="flex"
					justifyContent="center"
					alignItems="center"
				>
					<CircularProgress style={circularProgressStyle} />
				</Section>
			)}

			<Section>
				<ButtonStack>
					<Button
						variant="contained"
						color="secondary"
						disabled={isLoading}
						onClick={handleSaveModalOpen}
					>
						SAVE PROGRESS
					</Button>
					<Button
						variant="contained"
						color="primary"
						disabled={!isDirty || !isValid || isLoading}
						type="submit"
					>
						Next
					</Button>
				</ButtonStack>
			</Section>

			<Section>
				<SaveScriptModal
					openSaveScriptModal={openSaveModal}
					handleSaveScriptModal={handleSaveModalClose}
					quoteNumber={quoteData?.quoteInformation?.quoteNumber}
				/>
			</Section>
		</Page>
	);
}

export default AboutYouDisclosure;
